// Shared Broadcast-column rendering used by the scoreboard page's
// Today/Tomorrow tabs and the team-page schedule table. Keeping the
// link-resolution logic (scraped URL → NCAA fallback → em-dash) in
// one module so the two surfaces always render the column the same
// way; a divergent fix on one would silently regress the other.
import type { JSX } from "react";

// isDegenerateBroadcastURL identifies broadcaster links that resolve
// to a landing page rather than a specific live stream. Several
// school schedule pages publish the generic `espn.com/watch/`
// homepage as the "broadcast link" — a working URL that is useless
// to a viewer trying to find the stream. Detect these so the
// renderer can fall back to the NCAA game page, which carries the
// authoritative TV listing near kickoff.
export function isDegenerateBroadcastURL(url: string): boolean {
  if (!url) return true;
  const trimmed = url.replace(/\/+$/, "");
  if (/\/watch$/i.test(trimmed)) return true;
  const hasID = /\/player\/|\/live\/|gameId=|eventCalendarId=|\/id\//i.test(trimmed);
  if (/\/watch\?/i.test(trimmed) && !hasID) return true;
  return false;
}

// BroadcastCellProps bundles the per-row broadcast context. `url`
// and `channel` are optional — any combination of present/absent is
// a valid state the renderer handles. `gameID` powers the NCAA
// fallback URL.
export type BroadcastCellProps = {
  gameID: string;
  channel?: string;
  url?: string;
};

// renderBroadcastCell returns the cell contents for one row's
// Broadcast column. Four branches, in priority order:
//
//  1. Scraped URL that carries a stream-identifying path → clickable
//     channel text (or "Watch" when the channel is blank).
//  2. NCAA game page fallback when the scraped URL is landing-only
//     or missing. Labelled "Follow" (not "Watch") when the channel
//     is blank, because the NCAA page carries game status and TV
//     listings but is not itself a stream. Hover title says the same.
//  3. Channel-only text when we have a channel label but no viable
//     URL at all (only hits when `gameID` is empty — rare).
//  4. Em-dash when every signal is empty.
//
// Returns a JSX fragment that the caller wraps in a `<td>` with the
// appropriate layout class.
export function renderBroadcastCell({
  gameID,
  channel,
  url,
}: BroadcastCellProps): JSX.Element {
  const ch = channel ?? "";
  const scraped = url ?? "";
  const useful = Boolean(scraped) && !isDegenerateBroadcastURL(scraped);
  const fallback = gameID ? `https://www.ncaa.com/game/${gameID}` : "";
  const href = useful ? scraped : fallback;
  if (!href && !ch) {
    return <>—</>;
  }
  if (!href) {
    return <>{ch}</>;
  }
  // Label differs by branch: a real broadcast link reads "Watch"
  // when we don't know the channel; the NCAA fallback reads "Follow"
  // so a user clicking it isn't surprised by a status page in place
  // of a stream. When a channel name is known the label is the
  // channel in both branches — the user already sees the signal.
  const label = ch || (useful ? "Watch" : "Follow");
  const title = useful
    ? ch
      ? `Watch on ${ch}`
      : "Watch live stream"
    : ch
      ? `${ch} — follow game on NCAA.com`
      : "Follow game on NCAA.com";
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" title={title}>
      {label}
    </a>
  );
}
